"use client"

import type React from "react"
import { useState, useCallback, useEffect } from "react"
import { X, Upload, FileText, CheckCircle, AlertCircle, Users } from "lucide-react"
import type { DynamicSchema, DynamicUser, DynamicCreateRequest } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import { createDynamicUser } from "../services/api"
import { addToQueue } from "../services/queue"
import { useNetworkStatus } from "../hooks/useNetworkStatus"

interface FieldMapping {
  csvHeader: string
  schemaField: string | null
  confidence: number
}

interface DynamicBulkUploadModalProps {
  schema: DynamicSchema
  existingUsers: DynamicUser[]
  onClose: () => void
  onSuccess: (users: DynamicUser[]) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  cancelCurrentFetch: () => void
}

/**
 * Parser CSV compatible con RFC 4180:
 * - Maneja campos entre comillas con comas/tabs internos
 * - Maneja saltos de línea dentro de campos entrecomillados
 * - Maneja comillas escapadas ("")
 */
function parseCSV(input: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  let i = 0

  while (i < input.length) {
    const char = input[i]
    const next = input[i + 1]

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"'
        i += 2
      } else if (char === '"') {
        inQuotes = false
        i++
      } else {
        field += char
        i++
      }
    } else {
      if (char === '"' && field === '') {
        inQuotes = true
        i++
      } else if (char === ',' || char === '\t') {
        row.push(field.trim())
        field = ''
        i++
      } else if (char === '\r' && next === '\n') {
        row.push(field.trim())
        rows.push(row)
        row = []
        field = ''
        i += 2
      } else if (char === '\n') {
        row.push(field.trim())
        rows.push(row)
        row = []
        field = ''
        i++
      } else {
        field += char
        i++
      }
    }
  }

  // Último campo/fila
  if (field || row.length > 0) {
    row.push(field.trim())
    rows.push(row)
  }

  return rows
}

const DynamicBulkUploadModal: React.FC<DynamicBulkUploadModalProps> = ({
  schema,
  existingUsers,
  onClose,
  onSuccess,
  showToast,
  cancelCurrentFetch,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<any[]>([])
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])
  const [step, setStep] = useState<'upload' | 'mapping' | 'processing' | 'errors'>('upload')
  const [filteredCount, setFilteredCount] = useState<number>(0)
  const [duplicatesInfo, setDuplicatesInfo] = useState<{count: number, fieldName: string} | null>(null)
  const [duplicateRows, setDuplicateRows] = useState<Array<{nombre: string, email: string, code: string, fileRow: number}>>([])
  const [validationErrors, setValidationErrors] = useState<Array<{ index: number; email: string; errors?: string[]; error?: string; fileRow?: number }>>([])
  const [selectedForceRows, setSelectedForceRows] = useState<Set<number>>(new Set())
  const isOnline = useNetworkStatus()

  // Función para limpiar y normalizar texto para comparación
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9]/g, '') // Solo letras y números
  }

  // Función para encontrar la mejor coincidencia entre encabezado CSV y campo del schema
  const findBestMatch = (csvHeader: string): { field: string | null; confidence: number } => {
    const normalizedHeader = normalizeText(csvHeader)
    
    let bestMatch: { field: string | null; confidence: number } = { field: null, confidence: 0 }

    // Buscar en todos los campos del schema
    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      // Skip ID field
      if (fieldName === 'id') return

      const normalizedFieldName = normalizeText(fieldName)
      const normalizedLabel = normalizeText(fieldConfig.label)

      // Coincidencia exacta con nombre del campo
      if (normalizedHeader === normalizedFieldName) {
        bestMatch = { field: fieldName, confidence: 100 }
        return
      }

      // Coincidencia exacta con label
      if (normalizedHeader === normalizedLabel) {
        bestMatch = { field: fieldName, confidence: 95 }
        return
      }

      // Coincidencia parcial con nombre del campo
      if (normalizedHeader.includes(normalizedFieldName) || normalizedFieldName.includes(normalizedHeader)) {
        const confidence = Math.max(bestMatch.confidence, 80)
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: fieldName, confidence }
        }
      }

      // Coincidencia parcial con label
      if (normalizedHeader.includes(normalizedLabel) || normalizedLabel.includes(normalizedHeader)) {
        const confidence = Math.max(bestMatch.confidence, 75)
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: fieldName, confidence }
        }
      }

      // Coincidencias específicas comunes
      const commonMappings: { [key: string]: string } = {
        'email': 'email',
        'correo': 'email',
        'mail': 'email',
        'telefono': 'telefono',
        'phone': 'telefono',
        'tel': 'telefono',
        'nombre': 'nombre',
        'name': 'nombre',
        'firstname': 'nombre',
        'apellido': 'apellido',
        'lastname': 'apellido',
        'surname': 'apellido',
        'empresa': 'empresa',
        'company': 'empresa',
        'organizacion': 'empresa',
        'organization': 'empresa',
        'cargo': 'cargo',
        'position': 'cargo',
        'puesto': 'cargo',
        'job': 'cargo',
        'categoria': 'categoria',
        'category': 'categoria',
        'tipo': 'categoria',
        'type': 'categoria',
        'ciudad': 'ciudad',
        'city': 'ciudad',
        'pais': 'pais',
        'country': 'pais',
        'industria': 'industria',
        'industry': 'industria',
        'qr': 'codigo_qr',
        'codigoqr': 'codigo_qr',
        'codigo': 'codigo_qr'
      }

      const mappedField = commonMappings[normalizedHeader]
      if (mappedField && schema[mappedField]) {
        const confidence = 90
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: mappedField, confidence }
        }
      }
    })

    return bestMatch
  }

  // Procesar filas ya parseadas (string[][]) provenientes de CSV o XLSX
  const processParsedRows = useCallback((parsedRows: string[][]) => {
    if (parsedRows.length < 2) {
      showToast("error", "El archivo debe tener al menos una línea de encabezados y una línea de datos")
      return
    }

    const headers = parsedRows[0].map(h => (h || '').replace(/['"]/g, '').trim())
    const dataRows = parsedRows.slice(1)

    // Parsear datos
    const parsedData = dataRows.map((cols, index) => {
      if (cols.every(c => (c || '').trim() === '')) return null

      const rowObj: { [key: string]: string } = {}

      headers.forEach((header, i) => {
        rowObj[header] = (cols[i] || '').trim()
      })

      return { ...rowObj, _rowIndex: index + 2 } // +2 porque empezamos en línea 2
    }).filter(item => item !== null)

    if (parsedData.length === 0) {
      showToast("error", "No se encontraron datos válidos en el archivo")
      return
    }

    setCsvData(parsedData)

    // Crear mapeos automáticos
    const mappings: FieldMapping[] = headers.map(header => {
      const match = findBestMatch(header)
      return {
        csvHeader: header,
        schemaField: match.field,
        confidence: match.confidence
      }
    })

    setFieldMappings(mappings)
    setStep('mapping')

    showToast("success", `Archivo procesado: ${parsedData.length} registros encontrados`)
  }, [schema, showToast])

  // Procesar archivo CSV o XLSX
  const handleFileUpload = useCallback((file: File) => {
    const name = file.name.toLowerCase()
    const isCsv = name.endsWith('.csv')
    const isXlsx = name.endsWith('.xlsx')

    if (!isCsv && !isXlsx) {
      showToast("error", "Solo se permiten archivos CSV o XLSX")
      return
    }

    setCsvFile(file)

    if (isXlsx) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const buffer = e.target?.result as ArrayBuffer
          const { readXlsx } = await import("../lib/mini-xlsx-read")
          const parsedRows = await readXlsx(buffer)
          processParsedRows(parsedRows)
        } catch (error) {
          console.error('Error parsing XLSX:', error)
          showToast("error", "Error al procesar el archivo XLSX")
        }
      }
      reader.onerror = () => {
        showToast("error", "Error al leer el archivo")
      }
      reader.readAsArrayBuffer(file)
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        // Parser CSV compatible con RFC 4180
        const parsedRows = parseCSV(text)
        processParsedRows(parsedRows)
      } catch (error) {
        console.error('Error parsing CSV:', error)
        showToast("error", "Error al procesar el archivo CSV")
      }
    }
    reader.onerror = () => {
      showToast("error", "Error al leer el archivo")
    }
    reader.readAsText(file, 'UTF-8')
  }, [processParsedRows, showToast])

  // Calcular duplicados en tiempo real
  const calculateDuplicates = useCallback((mappings: FieldMapping[]) => {
    const fixedField = SchemaService.getFixedField(schema)
    const fixedFieldMapping = mappings.find(m => m.schemaField === fixedField)
    
    if (!fixedField || !fixedFieldMapping) {
      setFilteredCount(csvData.length)
      setDuplicatesInfo(null)
      return
    }

    // Obtener valores existentes del campo fixed
    const existingFixedValues = new Set<string>()
    existingUsers.forEach(user => {
      const value = SchemaService.getFieldValue(user, fixedField)
      if (value !== null && value !== undefined && value !== '') {
        existingFixedValues.add(String(value).toLowerCase().trim())
      }
    })

    // Contar duplicados y recolectar detalles
    let duplicatesCount = 0
    const duplicateDetails: Array<{nombre: string, email: string, code: string, fileRow: number}> = []

    // Encontrar mapeos de nombre, apellido y email para extraer info
    const nombreMapping = mappings.find(m => m.schemaField === 'nombre')
    const apellidoMapping = mappings.find(m => m.schemaField === 'apellido')
    const emailMapping = mappings.find(m => m.schemaField === 'email')

    const filteredData = csvData.filter(row => {
      const csvValue = row[fixedFieldMapping.csvHeader]
      if (!csvValue || csvValue.trim() === '') {
        return true
      }
      
      const normalizedCsvValue = String(csvValue).toLowerCase().trim()
      const isDuplicate = existingFixedValues.has(normalizedCsvValue)
      
      if (isDuplicate) {
        duplicatesCount++
        const nombre = [
          nombreMapping ? row[nombreMapping.csvHeader] || '' : '',
          apellidoMapping ? row[apellidoMapping.csvHeader] || '' : ''
        ].filter(Boolean).join(' ') || '-'
        duplicateDetails.push({
          nombre,
          email: emailMapping ? row[emailMapping.csvHeader] || '-' : '-',
          code: String(csvValue).trim(),
          fileRow: row._rowIndex || 0
        })
      }
      
      return !isDuplicate
    })

    setFilteredCount(filteredData.length)
    setDuplicateRows(duplicateDetails)
    setDuplicatesInfo(duplicatesCount > 0 ? {
      count: duplicatesCount,
      fieldName: schema[fixedField]?.label || fixedField
    } : null)
  }, [csvData, existingUsers, schema])

  // Efecto para calcular duplicados automáticamente cuando se cargan los datos
  useEffect(() => {
    if (csvData.length > 0 && fieldMappings.length > 0) {
      calculateDuplicates(fieldMappings)
    }
  }, [csvData, fieldMappings, calculateDuplicates])

  // Actualizar mapeo de campo
  const updateFieldMapping = (csvHeader: string, schemaField: string | null) => {
    const newMappings = fieldMappings.map(mapping => 
      mapping.csvHeader === csvHeader 
        ? { ...mapping, schemaField, confidence: schemaField ? 100 : 0 }
        : mapping
    )
    setFieldMappings(newMappings)
    
    // Recalcular duplicados con los nuevos mapeos
    calculateDuplicates(newMappings)
  }

  // Procesar y crear usuarios
  const handleCreateUsers = async (force?: boolean, onlyFileRows?: Set<number>) => {
    if (csvData.length === 0) return

    setStep('processing')

    try {
      // Si vienen filas seleccionadas (carga forzada parcial), trabajar solo con esas
      const sourceCsvData = onlyFileRows && onlyFileRows.size > 0
        ? csvData.filter(row => onlyFileRows.has(row._rowIndex))
        : csvData

      // Validar que al menos haya algunos mapeos
      const validMappings = fieldMappings.filter(m => m.schemaField !== null)
      if (validMappings.length === 0) {
        showToast("error", "Debe mapear al menos un campo")
        setStep('mapping')
        return
      }

      // Obtener el campo fixed del schema
      const fixedField = SchemaService.getFixedField(schema)
      
      // Verificar si hay mapeo del campo fixed
      const fixedFieldMapping = fieldMappings.find(m => m.schemaField === fixedField)
      
      // Filtrar duplicados basándose en el campo fixed si existe
      let filteredCsvData = sourceCsvData
      let duplicatesCount = 0
      
      if (fixedField && fixedFieldMapping) {
        console.log(`🔍 Verificando duplicados usando campo fixed: ${fixedField}`)
        
        // Obtener valores existentes del campo fixed de usuarios locales
        const existingFixedValues = new Set<string>()
        existingUsers.forEach(user => {
          const value = SchemaService.getFieldValue(user, fixedField)
          if (value !== null && value !== undefined && value !== '') {
            existingFixedValues.add(String(value).toLowerCase().trim())
          }
        })
        
        console.log(`📊 Valores existentes encontrados: ${existingFixedValues.size}`)
        
        // Filtrar filas del CSV que no tengan duplicados
        const originalCount = sourceCsvData.length
        filteredCsvData = sourceCsvData.filter(row => {
          const csvValue = row[fixedFieldMapping.csvHeader]
          if (!csvValue || csvValue.trim() === '') {
            return true // Mantener filas sin valor en campo fixed
          }
          
          const normalizedCsvValue = String(csvValue).toLowerCase().trim()
          const isDuplicate = existingFixedValues.has(normalizedCsvValue)
          
          if (isDuplicate) {
            console.log(`🚫 Fila duplicada omitida: ${fixedField} = ${csvValue}`)
            duplicatesCount++
          }
          
          return !isDuplicate
        })
        
        console.log(`✂️ Filtrado completado: ${originalCount} → ${filteredCsvData.length} (${duplicatesCount} duplicados omitidos)`)
        
        // Informar al usuario sobre duplicados encontrados
        if (duplicatesCount > 0) {
          showToast("info", `Se omitieron ${duplicatesCount} registros duplicados basados en ${schema[fixedField]?.label || fixedField}`)
        }
        
        // Si todas las filas fueron filtradas por duplicados
        if (filteredCsvData.length === 0) {
          showToast("error", "Todos los registros del CSV ya existen en el sistema")
          setStep('mapping')
          return
        }
      }

      // Construir usuarios usando los datos filtrados
      const users: DynamicCreateRequest[] = filteredCsvData.map((row) => {
        const user: DynamicCreateRequest = {
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          extra: {}
        }

        // Aplicar mapeos
        fieldMappings.forEach(mapping => {
          if (mapping.schemaField && row[mapping.csvHeader] !== undefined) {
            const value = row[mapping.csvHeader]
            const fieldConfig = schema[mapping.schemaField]
            
            // Procesar valor según tipo de campo
            let processedValue: any = value
            
            if (fieldConfig) {
              switch (fieldConfig.type) {
                case 'number':
                  processedValue = value === '' ? 0 : Number(value) || 0
                  break
                case 'boolean':
                  processedValue = ['true', '1', 'sí', 'si', 'yes', 'verdadero'].includes(value.toLowerCase())
                  break
                case 'string':
                case 'enum':
                default:
                  // Normalización específica por campo
                  if (mapping.schemaField === 'email') {
                    processedValue = value.toLowerCase().trim()
                  } else if (mapping.schemaField === 'telefono') {
                    processedValue = value.replace(/[^\d\s()+-]/g, "")
                  } else if (mapping.schemaField === 'nombre' || mapping.schemaField === 'apellido') {
                    processedValue = value.replace(/\s+/g, " ").trim()
                  } else {
                    processedValue = value.trim()
                  }
                  break
              }
            }

            // Asignar a campo principal o extra
            const mainFields = ['nombre', 'apellido', 'email', 'telefono']
            if (mainFields.includes(mapping.schemaField)) {
              (user as any)[mapping.schemaField] = processedValue
            } else {
              user.extra[mapping.schemaField] = processedValue
            }
          }
        })

        return user
      })

      // Cancelar fetch actual
      cancelCurrentFetch()

      // Crear usuarios usando el sistema existente
      const createUsersPayload = users

      const createUsers = async () => {
        if (isOnline) {
          try {
            const result = await createDynamicUser(createUsersPayload, { force })
            
            // Manejar respuesta
            if (Array.isArray(result)) {
              // Respuesta exitosa con array de usuarios creados
              showToast("success", `${result.length} usuarios creados exitosamente`)
              onSuccess(result)
              onClose()
            } else {
              // Manejo genérico de respuesta exitosa
              showToast("success", "Usuarios creados exitosamente")
              onClose()
            }
          } catch (networkError: any) {
            console.warn("Error en creación masiva:", networkError)
            
            // Error 422: error de validación (datos inválidos: falta nombre/apellido o email inválido).
            // Es un error permanente: NO se agrega a la cola (volvería a fallar en cada sincronización)
            // y se muestra el detalle por fila de forma persistente para que el usuario lo corrija.
            const status = networkError?.status
            if (status === 422 || (networkError.message && networkError.message.includes("422"))) {
              const rows =
                networkError?.data?.data?.errors ??
                networkError?.data?.errors ??
                []
              // El backend devuelve "index" = posición dentro del lote enviado (datos ya filtrados).
              // Lo mapeamos a "_rowIndex" = número de fila real del archivo cargado para ubicarlo rápido.
              const mappedRows = (Array.isArray(rows) ? rows : []).map((r: any) => ({
                ...r,
                fileRow: typeof r.index === 'number' ? filteredCsvData[r.index]?._rowIndex : undefined,
              }))
              setValidationErrors(mappedRows)
              setSelectedForceRows(new Set())
              setStep('errors')
              return
            }

            // Error 207: algunos usuarios no se pudieron crear (duplicados). Tampoco se encola.
            if (networkError.message && networkError.message.includes("207")) {
              showToast("error", "Algunos usuarios no se pudieron crear (posibles duplicados). Revisa los datos e intenta nuevamente.")
              setStep('mapping') // Volver al mapeo para que el usuario pueda revisar
              return
            }
            
            // Para otros errores de red, agregar a la cola
            await addToQueue({
              type: "create_dynamic_user_bulk",
              data: createUsersPayload as any,
              timestamp: Date.now(),
            })
            showToast("info", "Usuarios guardados localmente, se sincronizarán cuando haya conexión")
            onClose()
          }
        } else {
          await addToQueue({
            type: "create_dynamic_user_bulk",
            data: createUsersPayload as any,
            timestamp: Date.now(),
          })
          showToast("info", "Usuarios guardados localmente, se sincronizarán cuando haya conexión")
          onClose()
        }
      }

      await createUsers()
      
    } catch (error) {
      console.error('Error creating users:', error)
      showToast("error", "Error al crear los usuarios")
      setStep('mapping')
    } finally {
    }
  }

  // Reintentar carga excluyendo los registros inválidos que reportó el backend
  const handleCreateUsersSkippingInvalid = async () => {
    // Filtrar csvData para excluir las filas inválidas por _rowIndex
    const invalidFileRows = new Set(
      validationErrors
        .map(e => e.fileRow)
        .filter((r): r is number => typeof r === 'number')
    )

    // Crear nuevo csvData sin las filas inválidas
    const cleanedCsvData = csvData.filter(row => !invalidFileRows.has(row._rowIndex))

    if (cleanedCsvData.length === 0) {
      showToast("error", "No quedan registros válidos para cargar")
      return
    }

    // Reemplazar csvData temporalmente y ejecutar la carga
    const originalCsvData = csvData
    setCsvData(cleanedCsvData)

    // Necesitamos llamar handleCreateUsers con los datos limpios directamente
    // ya que setCsvData es async. Hacemos la lógica inline.
    setStep('processing')
    try {
      const validMappings = fieldMappings.filter(m => m.schemaField !== null)
      const fixedField = SchemaService.getFixedField(schema)
      
      // Filtrar duplicados del set limpio
      let filteredCsvData = cleanedCsvData
      if (fixedField) {
        const fixedFieldMapping = fieldMappings.find(m => m.schemaField === fixedField)
        if (fixedFieldMapping) {
          const existingFixedValues = new Set<string>()
          existingUsers.forEach(user => {
            const val = SchemaService.getFieldValue(user, fixedField)
            if (val) existingFixedValues.add(String(val).trim().toLowerCase())
          })
          filteredCsvData = cleanedCsvData.filter(row => {
            const csvValue = String(row[fixedFieldMapping.csvHeader] || '').trim().toLowerCase()
            return !csvValue || !existingFixedValues.has(csvValue)
          })
        }
      }

      if (filteredCsvData.length === 0) {
        showToast("error", "No quedan registros válidos para cargar")
        setCsvData(originalCsvData)
        setStep('mapping')
        return
      }

      // Construir usuarios
      const users = filteredCsvData.map(row => {
        const user: any = { nombre: '', apellido: '', email: '', telefono: '', extra: {} }
        validMappings.forEach(mapping => {
          if (mapping.schemaField) {
            const value = row[mapping.csvHeader] || ''
            const mainFields = ['nombre', 'apellido', 'email', 'telefono']
            if (mainFields.includes(mapping.schemaField)) {
              user[mapping.schemaField] = value.trim()
            } else {
              user.extra[mapping.schemaField] = value.trim()
            }
          }
        })
        return user
      })

      cancelCurrentFetch()

      const result = await createDynamicUser(users)
      if (Array.isArray(result)) {
        showToast("success", `${result.length} usuarios creados exitosamente (${validationErrors.length} inválidos omitidos)`)
        onSuccess(result)
        onClose()
      } else {
        showToast("success", "Usuarios creados exitosamente")
        onClose()
      }
    } catch (error: any) {
      console.error('Error creating valid users:', error)
      const status = error?.status
      if (status === 422) {
        showToast("error", "Aún hay registros inválidos. Revisa el archivo.")
      } else {
        showToast("error", "Error al crear los usuarios")
      }
      setCsvData(originalCsvData)
      setStep('mapping')
    }
  }

  // Obtener campos disponibles del schema (excluyendo ID)
  const availableFields = Object.entries(schema)
    .filter(([fieldName]) => fieldName !== 'id')
    .map(([fieldName, fieldConfig]) => ({
      value: fieldName,
      label: fieldConfig.label
    }))

  // Etiqueta amigable para un campo (usa el label del schema si existe)
  const getFieldLabel = (field: string): string => schema[field]?.label || field

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white/40 backdrop-blur-sm px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Carga Masiva de Usuarios
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Step Upload */}
            {step === 'upload' && (
              <div className="space-y-6">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <h4 className="mt-2 text-lg font-medium text-gray-900">Subir archivo CSV o XLSX</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Selecciona un archivo CSV o Excel (.xlsx) con los datos de los usuarios
                  </p>
                </div>

                {/* File upload area */}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors"
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files[0]
                    if (file) handleFileUpload(file)
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <FileText className="mx-auto h-8 w-8 text-gray-400 mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Arrastra tu archivo CSV o XLSX aquí o
                    </p>
                    <label className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-800 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 cursor-pointer">
                      <Upload className="h-4 w-4 mr-2" />
                      Seleccionar archivo
                      <input
                        type="file"
                        accept=".csv,.xlsx"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleFileUpload(file)
                        }}
                        className="sr-only"
                      />
                    </label>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Requisitos del archivo CSV
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Primera fila debe contener los encabezados</li>
                          <li>Separado por comas (,) o tabulaciones</li>
                          <li>Codificación UTF-8 recomendada</li>
                          <li>Los encabezados se mapearán automáticamente con los campos disponibles</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step Mapping */}
            {step === 'mapping' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Mapeo de Campos</h4>
                    <p className="text-sm text-gray-500">
                      Archivo: {csvFile?.name} - {csvData.length} registros
                    </p>
                  </div>
                  <button
                    onClick={() => setStep('upload')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Cambiar archivo
                  </button>
                </div>

                <div className="bg-amber-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-amber-700">
                        Revisa y corrige el mapeo automático de campos. Los campos sin mapear se ignorarán.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información de duplicados */}
                {duplicatesInfo && (
                  <div className={`p-4 rounded-xl ${filteredCount === 0 ? 'bg-red-50' : 'bg-orange-50'}`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className={`h-5 w-5 ${filteredCount === 0 ? 'text-red-400' : 'text-orange-400'}`} />
                      </div>
                      <div className="ml-3">
                        <h3 className={`text-sm font-medium ${filteredCount === 0 ? 'text-red-800' : 'text-orange-800'}`}>
                          {filteredCount === 0 ? 'Todos los registros son duplicados' : 'Duplicados detectados'}
                        </h3>
                        <p className={`text-sm mt-1 ${filteredCount === 0 ? 'text-red-700' : 'text-orange-700'}`}>
                          {filteredCount === 0 ? (
                            <>
                              <strong>Todos los {duplicatesInfo.count} registros del CSV ya existen</strong> en el sistema basados en el campo <strong>{duplicatesInfo.fieldName}</strong>.
                              No se puede continuar con la carga masiva.
                            </>
                          ) : (
                            <>
                              Se encontraron <strong>{duplicatesInfo.count} registros duplicados</strong> basados en el campo <strong>{duplicatesInfo.fieldName}</strong>.
                              Solo se crearán <strong>{filteredCount} usuarios nuevos</strong>, omitiendo automáticamente los duplicados.
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tabla de registros duplicados */}
                {duplicateRows.length > 0 && duplicatesInfo && (
                  <details className="border border-orange-200 rounded-xl overflow-hidden">
                    <summary className="px-4 py-3 bg-orange-50 cursor-pointer text-sm font-medium text-orange-800 hover:bg-orange-100 transition-colors">
                      Ver {duplicateRows.length} registros duplicados
                    </summary>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Fila</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Nombre</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">{duplicatesInfo.fieldName}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {duplicateRows.map((row, i) => (
                            <tr key={i} className="hover:bg-orange-50/50">
                              <td className="px-4 py-2 text-gray-600">{row.fileRow}</td>
                              <td className="px-4 py-2 text-gray-800">{row.nombre}</td>
                              <td className="px-4 py-2 text-gray-800 break-all">{row.email}</td>
                              <td className="px-4 py-2 text-gray-600 font-mono text-xs">{row.code}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}

                {/* Información adicional cuando no hay duplicados pero sí campo fixed */}
                {!duplicatesInfo && filteredCount === csvData.length && SchemaService.getFixedField(schema) && fieldMappings.find(m => m.schemaField === SchemaService.getFixedField(schema)) && (
                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Sin duplicados detectados
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          Todos los <strong>{csvData.length} registros</strong> son únicos basados en el campo <strong>{schema[SchemaService.getFixedField(schema)!]?.label || SchemaService.getFixedField(schema)}</strong>.
                          Se crearán todos los usuarios.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview de datos */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h5 className="text-sm font-medium text-gray-900 flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-gray-500" />
                      Vista previa (primeros 3 registros)
                    </h5>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {fieldMappings.map((mapping, idx) => (
                            <th key={idx} className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">
                              <div className="space-y-1">
                                <div className="text-gray-900">{mapping.csvHeader}</div>
                                {mapping.schemaField && (
                                  <div className="text-green-600 text-[10px] flex items-center">
                                    → {getFieldLabel(mapping.schemaField)}
                                  </div>
                                )}
                                {!mapping.schemaField && (
                                  <div className="text-gray-400 text-[10px]">
                                    (sin mapear)
                                  </div>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {csvData.slice(0, 3).map((row, rowIdx) => (
                          <tr key={rowIdx} className="hover:bg-gray-50">
                            {fieldMappings.map((mapping, colIdx) => (
                              <td key={colIdx} className="px-3 py-2 text-gray-800 max-w-xs truncate">
                                {row[mapping.csvHeader] || <span className="text-gray-400 italic">vacío</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {csvData.length > 3 && (
                    <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600 text-center">
                      ... y {csvData.length - 3} registros más
                    </div>
                  )}
                </div>

                {/* Mappings */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {fieldMappings.map((mapping, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Encabezado CSV
                        </label>
                        <div className="p-2 bg-gray-50 rounded-lg text-sm">
                          {mapping.csvHeader}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Campo del Sistema
                        </label>
                        <select
                          value={mapping.schemaField || ''}
                          onChange={(e) => updateFieldMapping(mapping.csvHeader, e.target.value || null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Sin mapear</option>
                          {availableFields.map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confianza
                        </label>
                        <div className="flex items-center space-x-2">
                          <div className={`h-2 w-full rounded-full ${
                            mapping.confidence >= 80 ? 'bg-green-200' : 
                            mapping.confidence >= 60 ? 'bg-yellow-200' : 'bg-red-200'
                          }`}>
                            <div 
                              className={`h-2 rounded-full ${
                                mapping.confidence >= 80 ? 'bg-green-500' : 
                                mapping.confidence >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${mapping.confidence}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-500">{mapping.confidence}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleCreateUsers()}
                    disabled={fieldMappings.filter(m => m.schemaField !== null).length === 0 || filteredCount === 0}
                    className="inline-flex items-center px-4 py-2 bg-green-600/20 backdrop-blur-sm border border-green-400/30 text-green-800 text-sm font-medium rounded-xl hover:bg-green-600/30 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Crear {filteredCount > 0 ? filteredCount : csvData.length} Usuarios
                    {duplicatesInfo && (
                      <span className="ml-1 text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full">
                        {duplicatesInfo.count} omitidos
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step Processing */}
            {step === 'processing' && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                  </div>
                  <h4 className="mt-4 text-lg font-medium text-gray-900">Creando usuarios...</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Procesando {csvData.length} registros. Esto puede tomar unos momentos.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        {isOnline 
                          ? "Creando usuarios en línea..." 
                          : "Sin conexión: los usuarios se guardarán localmente y se sincronizarán cuando haya conexión"
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step Errors (errores de validación de la carga masiva) */}
            {step === 'errors' && (
              <div className="space-y-6">
                <div className="bg-red-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-red-800">
                        No se pudo crear ningún usuario
                      </h4>
                      <p className="text-sm text-red-700 mt-1">
                        {validationErrors.length > 0
                          ? <>Se encontraron errores de validación en <strong>{validationErrors.length} registro{validationErrors.length === 1 ? '' : 's'}</strong>. Puedes cargar solo los registros válidos o corregir el archivo.</>
                          : <>Los datos enviados no superaron la validación. Revisa que <strong>nombre, apellido y email</strong> sean válidos y vuelve a intentarlo.</>
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {validationErrors.length > 0 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-700 w-10">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300"
                                checked={validationErrors.length > 0 && validationErrors.every(r => typeof r.fileRow !== 'number' || selectedForceRows.has(r.fileRow))}
                                onChange={(e) => {
                                  const fileRows = validationErrors
                                    .map(r => r.fileRow)
                                    .filter((r): r is number => typeof r === 'number')
                                  setSelectedForceRows(e.target.checked ? new Set(fileRows) : new Set())
                                }}
                              />
                            </th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Fila del archivo</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Campos con error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {validationErrors.map((row, i) => (
                            <tr key={i} className="hover:bg-red-50/50">
                              <td className="px-4 py-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300"
                                  disabled={typeof row.fileRow !== 'number'}
                                  checked={typeof row.fileRow === 'number' && selectedForceRows.has(row.fileRow)}
                                  onChange={(e) => {
                                    if (typeof row.fileRow !== 'number') return
                                    setSelectedForceRows(prev => {
                                      const next = new Set(prev)
                                      if (e.target.checked) next.add(row.fileRow!)
                                      else next.delete(row.fileRow!)
                                      return next
                                    })
                                  }}
                                />
                              </td>
                              <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                                {typeof row.fileRow === 'number'
                                  ? row.fileRow
                                  : (typeof row.index === 'number' ? row.index + 1 : '-')}
                              </td>
                              <td className="px-4 py-2 text-gray-800 break-all">
                                {row.email && row.email !== 'unknown' ? row.email : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="px-4 py-2">
                                {Array.isArray(row.errors) && row.errors.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {row.errors.map((field, fi) => (
                                      <span key={fi} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        {getFieldLabel(field)}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-red-700 text-xs">{row.error || 'Error de validación'}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setValidationErrors([])
                        handleCreateUsersSkippingInvalid()
                      }}
                      className="inline-flex items-center px-4 py-2 bg-green-600/20 backdrop-blur-sm border border-green-400/30 text-green-800 text-sm font-medium rounded-xl hover:bg-green-600/30 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Cargar solo válidos ({csvData.length - validationErrors.length})
                    </button>
                    {selectedForceRows.size > 0 && (
                      <button
                        onClick={() => {
                          const rows = new Set(selectedForceRows)
                          setValidationErrors([])
                          setSelectedForceRows(new Set())
                          handleCreateUsers(true, rows)
                        }}
                        className="inline-flex items-center px-4 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-400/30 text-amber-800 text-sm font-medium rounded-xl hover:bg-amber-600/30 hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300"
                        title="Carga solo los registros seleccionados sin validar nombre, apellido ni email"
                      >
                        ⚠️ Forzar carga de seleccionados ({selectedForceRows.size})
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setValidationErrors([])
                        setSelectedForceRows(new Set())
                        handleCreateUsers(true)
                      }}
                      className="inline-flex items-center px-4 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-400/30 text-amber-800 text-sm font-medium rounded-xl hover:bg-amber-600/30 hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300"
                      title="Carga todos los registros sin validar nombre, apellido ni email"
                    >
                      ⚠️ Forzar carga de todos ({csvData.length})
                    </button>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setValidationErrors([])
                        setCsvFile(null)
                        setCsvData([])
                        setFieldMappings([])
                        setStep('upload')
                      }}
                      className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300"
                    >
                      Cargar otro archivo
                    </button>
                    <button
                      onClick={() => {
                        setValidationErrors([])
                        setStep('mapping')
                      }}
                      className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-800 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300"
                    >
                      Volver al mapeo
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicBulkUploadModal
